import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {SampleDesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/SampleDesignResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {OhmParseInputResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    FlowBlock,
    INPUT_COMPONENT_NAME,
    OUTPUT_COMPONENT_NAME
} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {
    FormulaInputTokenType,
    ICellReferenceFormulaToken,
    ILinearFormulaToken
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {SheetSlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSlotLocation';
import {AnySheet, AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {FrameDetailsEntry} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetEditorState';
import {Box, BoxTypes} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {
    ISampleDesignState,
    SampleDesignCellBehavior,
    SampleDesignState
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {RouteUtil} from '@mintdata-wsp/wsp/ui/routes/RouteUtil';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {FrameCellCoordinate, FramePath} from '@mintdata/mint-kit/api/values/frames/Frame';
import bind from 'bind-decorator';
import _find from 'lodash/find';
import {action} from 'mobx';
import {RouteComponentProps} from 'react-router';

interface SampleDesignParam
{
    fnType:any;
    toBoolean?:boolean;
}

export class SampleDesignController
{
    public async init(props:RouteComponentProps<any>):Promise<void>
    {
        this.initSampleDesignParams();

        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign.showFlowEditor )
            this.initSampleFlowEditor(props);
        else
            this.initSampleSurfaceAndSpreadSheet(props);
    }

    public async initSampleSurfaceAndSpreadSheet(props:RouteComponentProps<any>):Promise<void>
    {
        this.checkSpreadsheetVisibilityBeforeOpenDesign();

        const {artifacts, openDesign, workbench, flow} = controllers.ui;

        controllers.navigation.setCurrentRouteProps(props);

        const {designId, projectId} = this._getUrlParams(props);
        const design = openDesign.getOpenDesign();

        const {sampleDesign} = rootState.ui.visualDesigner;

        if( !design || design.designRef.designId !== designId )
        {
            openDesign.setOpenDesign(null);

            if( sampleDesign )
                artifacts.resetArtifactsLoadingPromise();
        }

        if( !openDesign.getOpenDesign() )
        {
            flow.clearFlows();

            // if (!sampleDesign)
            //     await Promise.all([
            //         // artifacts.ensureVisualArtifactsAreLoaded(),
            //         // artifacts.ensureCloudArtifactsAreLoaded(),
            //         // flow.ensureFlowsAreLoaded(projectId, designId)
            //     ]);

            const isScreenshotMode = window.location.pathname.includes('take-screenshot');
            const urlParams = new URLSearchParams(window.location.search);

            if( isScreenshotMode )
            {
                await openDesign.openSampleDesign(
                    designId,
                    this.checkAfterOpenDesignInitialized,
                    this.checkSpreadsheetVisibilityAfterOpenDesign,
                    true,
                    projectId,
                    urlParams.get('publicToken')
                );
            }
            else
            {
                await openDesign.openSampleDesign(
                    designId,
                    this.checkAfterOpenDesignInitialized,
                    this.checkSpreadsheetVisibilityAfterOpenDesign
                );
            }

            // When design is being opened, it's project becomes last used project.
            // console.log('%c rootState.userSession =', 'background:#f00;color:#ff0;', rootState.userSession);
            // const orgSid = rootState.userSession.currentOrganization.siteId;
            // LocalStateUtil.storeSelectedProject(projectId, orgSid);

            if( sampleDesign && sampleDesign.showSurface && openDesign.getOpenDesign() )
            {
                artifacts.startLoadingBundlesForVisualArtifacts();
                workbench.filterVisibleVisualComponents(null);
            }
        }

        controllers.ui.anyPage.disablePageLoader();
    }

    public async initSampleFlowEditor(props:RouteComponentProps<any>):Promise<void>
    {
        const {artifacts, flow, flowComponentsPanel, openDesign} = controllers.ui;
        const {designId} = this._getUrlParams(props);

        await artifacts.ensureCloudArtifactsAreLoaded();

        flowComponentsPanel.onCloudArtifactsLoaded();

        await flow.ensureFlowsAreLoaded(null, designId, true);

        this.checkFlowEditorVisibilityAfterOpenDesign();

        controllers.ui.anyPage.disablePageLoader();
    }

    private _getUrlParams(props:RouteComponentProps<any>):{designId:number, projectId?:number}
    {
        const {designId, projectId} = props.match.params;

        return {
            designId: Number(designId),
            projectId: projectId ? Number(projectId) : undefined,
        };
    }

    @action.bound
    public initSampleDesignParams():void
    {
        const {openDesign} = controllers.ui;

        openDesign.setSampleDesignMode();

        const tmpParams:ISampleDesignState = SampleDesignState.defaultParams();

        const _paramType = (fnType:any, toBoolean:boolean = false) =>
        {
            return {fnType, toBoolean};
        };

        const conversionTable:{[key:string]:SampleDesignParam} = {
            'embedded': _paramType(Number, true),

            'showSurface': _paramType(Number, true),
            'showPropPanel': _paramType(Number, true),
            'showOutlinePanel': _paramType(Number, true),
            'selectedPage': _paramType(String),
            'selectedComponentName': _paramType(String),

            'showSpreadsheet': _paramType(Number, true),
            'showStyleBar': _paramType(Number, true),
            'spreadsheetWidthPercent': _paramType(Number),
            'selectedSheet': _paramType(String),
            'selectedSheetRef': _paramType(String),

            'showFlowEditor': _paramType(Number, true),
            'showFlowPropPanel': _paramType(Number, true),
            'selectedFlowBlock': _paramType(String),
            'canSwitchToFlow': _paramType(Number, true),

            'cellBehavior': _paramType(String),
            'formulaCursorOffset': _paramType(Number),
            'showFormulaTypeahead': _paramType(Number, true),
            'showFormulaDocs': _paramType(Number, true),
            'selectedNestedPath': _paramType(String),
        };

        const _get = (paramName:string, param:SampleDesignParam):any =>
        {
            const value = param.fnType(RouteUtil.getQueryVariable(paramName));

            tmpParams[paramName] = param.toBoolean ? !!value : value;
        };

        const _set = (paramName:string) =>
        {
            const value = tmpParams[paramName];

            if( value !== null && (!isNaN(value) || value.length) )
            {
                openDesign.setSampleDesignParam(paramName, value);
            }
        };

        Object.entries(conversionTable).forEach(([k, v]) => _get(k, v));

        _set('embedded');

        if( tmpParams.showSurface )
        {
            _set('showSurface');

            _set('showPropPanel');
            _set('showOutlinePanel');
            _set('selectedPage');
            _set('selectedComponentName');
        }

        if( tmpParams.showFlowEditor )
        {
            _set('showFlowEditor');

            _set('showFlowPropPanel');
            _set('selectedFlowBlock');
        }

        _set('canSwitchToFlow');

        _set('showSpreadsheet');

        _set('spreadsheetWidthPercent');
        _set('showStyleBar');
        _set('selectedSheet');
        _set('selectedSheetRef');

        if( tmpParams.cellBehavior )
        {
            _set('cellBehavior');

            if( tmpParams.cellBehavior === SampleDesignCellBehavior.openFormulaEditor )
            {
                _set('formulaCursorOffset');
                _set('showFormulaTypeahead');
                _set('showFormulaDocs');
            }

            if( tmpParams.cellBehavior === SampleDesignCellBehavior.openNestedSpreadsheet )
            {
                _set('selectedNestedPath');
            }
        }

        const {sampleDesign} = rootState.ui.visualDesigner;
    }

    @action.bound
    public checkSpreadsheetVisibilityBeforeOpenDesign():void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( !sampleDesign ) return;

        const {cellSettingsBar} = controllers.ui.spreadsheets;

        cellSettingsBar.setBarVisibility(sampleDesign.showStyleBar);

        const {workbenchVisibility} = controllers.ui;

        if( sampleDesign.showSpreadsheet )
        {
            if( !sampleDesign.showSurface )
            {
                workbenchVisibility.toggleSpreadsheetFullScreen();
            }
            else if( sampleDesign.spreadsheetWidthPercent === 100 )
            {
                workbenchVisibility.toggleSpreadsheetFullScreen();
            }
            else if( sampleDesign.spreadsheetWidthPercent === 0 )
            {
                workbenchVisibility.setSpreadsheetVisibility(false);
            }
            else if( sampleDesign.spreadsheetWidthPercent )
            {
                workbenchVisibility.setGridRightColumnWidth(sampleDesign.spreadsheetWidthPercent);
            }
        }
        else
        {
            workbenchVisibility.setSpreadsheetVisibility(false);
        }
    }

    @action.bound
    public checkSpreadsheetVisibilityAfterOpenDesign():void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( !sampleDesign ) return;

        let sheetId = this._checkSelectedSheet();

        this._checkSelectedSheetRef(sheetId);
        this._checkOpenFormulaEditor(sheetId);
        this._checkOpenNestedSpreadsheet(sheetId);
    }

    @action.bound
    public checkFlowEditorVisibilityAfterOpenDesign():void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( !sampleDesign.showFlowEditor ) return;

        const {ui} = controllers;
        const {flow} = ui;

        const flows = flow.getFlows();

        if( !flows.length ) return;

        flow.openFlow(flows[0].id);

        if( sampleDesign.showFlowPropPanel )
        {
            const {flowTopBar} = controllers.ui;

            flowTopBar.togglePropertiesPanelVisibility();
        }

        if( sampleDesign.selectedFlowBlock )
        {
            const {flowBlocks} = flows[0];

            let selectedBlock = _find(flowBlocks, (flowBlock:FlowBlock) =>
            {
                return flowBlock.instanceName === sampleDesign.selectedFlowBlock;
            });

            let visibleBlocks = [];

            if( !selectedBlock )
            {
                visibleBlocks = flowBlocks.filter((flowBlock:FlowBlock) => !flowBlock.isEntryBlock);

                visibleBlocks.forEach((flowBlock:FlowBlock) =>
                {
                    if( selectedBlock ) return;

                    if( flowBlock.name !== INPUT_COMPONENT_NAME && flowBlock.name !== OUTPUT_COMPONENT_NAME )
                        selectedBlock = flowBlock;
                });
            }

            if( !selectedBlock && visibleBlocks.length )
                selectedBlock = visibleBlocks[0];

            if( !selectedBlock ) return;

            const selectionController = controllers.ui.flowSelection;
            selectionController.setSingleBlockSelection(selectedBlock);
        }
    }

    @action.bound
    public checkAfterOpenDesignInitialized():void
    {
        this.checkFlowEditorVisibilityAfterOpenDesign();

        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign && sampleDesign.showSurface )
        {
            const {workbenchVisibility} = controllers.ui;
            const {designState} = commonControllers;

            if( sampleDesign.selectedPage )
            {
                designState.setActivePageByName(sampleDesign.selectedPage);
            }

            this._checkSelectedComponentName();

            if( sampleDesign.showPropPanel )
            {
                workbenchVisibility.setPropertiesPanelVisibility(true);
            }

            if( sampleDesign.showOutlinePanel )
            {
                workbenchVisibility.setPagesPanelVisibility(true);
            }
        }
    }

    @bind
    private _checkSelectedSheet():SheetId
    {
        const {sampleDesign} = rootState.ui.visualDesigner;
        const {tabs} = commonControllers.spreadsheets;

        const openDesign = commonControllers.designState.getOpenDesign();
        const {rootWorkbook} = openDesign.designState.spreadsheets;

        let sheetId:SheetId = null;

        if( sampleDesign.selectedSheet )
        {
            const {workbook} = controllers.ui.spreadsheets;
            const allSheets:AnySheet[] = workbook.getAllSheets(rootWorkbook.id);

            allSheets.forEach((sheet:Sheet) =>
            {
                if( sheetId ) return;

                if( sheet.name === sampleDesign.selectedSheet )
                    sheetId = sheet.id;
            });

            if( sheetId )
                tabs.setActiveSheet(rootWorkbook.id, sheetId);
        }

        if( !sheetId )
        {
            const sheet:AnySheet = tabs.getActiveSheet(rootWorkbook.id);

            if( sheet )
                sheetId = sheet.id;
        }

        return sheetId;
    }

    @bind
    private _checkSelectedSheetRef(sheetId:SheetId):void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( !sampleDesign.selectedSheetRef ) return;

        const {mesh, workbook} = controllers.ui.spreadsheets;
        const ctx = workbook.getMeshContextBySheetId(sheetId);
        const meshState:MeshState = mesh.getState(ctx);

        const formulaEvalResult:OhmParseInputResult = FormulaEngine.getInstance().parseInputA1(
            '=' + sampleDesign.selectedSheetRef,
            new SlotLocation(0, 0)
        );
        const {formulaInput} = controllers.ui.spreadsheets;
        const formulaTokens = formulaInput.getPlainFormulaTokens(formulaEvalResult.result);

        let locationFrom = new MeshLocation(0, 0);
        let locationTo = new MeshLocation(0, 0);

        formulaTokens && formulaTokens.map((formulaToken:ILinearFormulaToken) =>
        {
            if( formulaToken.token.type === FormulaInputTokenType.CellReference )
            {
                const RCLocation = (formulaToken.token as ICellReferenceFormulaToken).location;

                locationFrom = new MeshLocation(RCLocation.columnOffset, RCLocation.rowOffset);
                locationTo = locationFrom;
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRange )
            {
                const RCRange = (formulaToken.token as ICellReferenceFormulaToken).range;

                locationFrom = new MeshLocation(RCRange.startLocation.columnOffset, RCRange.startLocation.rowOffset);
                locationTo = new MeshLocation(RCRange.endLocation.columnOffset, RCRange.endLocation.rowOffset);
            }
        });

        const {meshSelection} = controllers.ui.spreadsheets;
        meshSelection.selectCell(ctx, locationFrom);

        if( sampleDesign.cellBehavior != SampleDesignCellBehavior.openNestedSpreadsheet )
        {
            meshState.details.selectionRangeFrom = locationFrom;
            meshState.details.selectionRangeTo = locationTo;
        }

        // mesh.focusMesh(sheetId);
    }

    @bind
    private _checkOpenFormulaEditor(sheetId:SheetId):void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign.cellBehavior !== SampleDesignCellBehavior.openFormulaEditor ) return;

        let initialChar = null;
        let cursorPosition = 0;
        let isShowDoc = false;

        if( sampleDesign.formulaCursorOffset )
        {
            cursorPosition = sampleDesign.formulaCursorOffset;
        }

        if( sampleDesign.showFormulaDocs && !sampleDesign.showFormulaTypeahead )
        {
            isShowDoc = sampleDesign.showFormulaTypeahead;
        }

        const {mesh, workbook} = controllers.ui.spreadsheets;
        const ctx = workbook.getMeshContextBySheetId(sheetId);
        mesh.startEditingCell(ctx, initialChar, cursorPosition, isShowDoc);

        if( sampleDesign.showFormulaTypeahead )
        {
            requestAnimationFrame(() =>
            {
                const {meshInput} = controllers.ui.spreadsheets;
                meshInput.showFunctionTypeahead(ctx);
            });
        }
    }

    @bind
    private _checkOpenNestedSpreadsheet(sheetId:SheetId):void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign.cellBehavior !== SampleDesignCellBehavior.openNestedSpreadsheet ) return;

        const {mesh, workbook} = controllers.ui.spreadsheets;
        const ctx = workbook.getMeshContextBySheetId(sheetId);
        const meshState:MeshState = mesh.getState(ctx);

        const {activeCell} = meshState.details;

        const openDesign = commonControllers.designState.getOpenDesign();
        const {rootWorkbook} = openDesign.designState.spreadsheets;

        const {frameDetails} = controllers.ui.spreadsheets;
        const {sheetCtrl} = ctx;
        const slotLocation:SlotLocation = new SlotLocation(activeCell.colIndex, activeCell.rowIndex);
        const slot:AnySlot = sheetCtrl.findSlot(sheetId, slotLocation);

        if( slot )
        {
            const reEvaluateFn = slot.isLazy ? sheetCtrl.reEvaluateLazySlot : sheetCtrl.forceEvaluateSlot;

            reEvaluateFn(slot.sheet.id, slot.location).then(() =>
            {
                const framePath:FramePath = this._getSelectedNestedPath();

                if( framePath && framePath.length )
                {
                    setTimeout(() =>
                    {
                        frameDetails.enterNestedViewByPath(
                            rootWorkbook.id,
                            sheetId,
                            slot.location,
                            framePath
                        ).then((sheetSlotLocation:SheetSlotLocation) =>
                        {
                            if( !sheetSlotLocation ) return;

                            const {meshSelection} = controllers.ui.spreadsheets;
                            let {colIndex, rowIndex} = sheetSlotLocation;

                            if( colIndex < 0 || rowIndex < 0 )
                            {
                                colIndex = 0;
                                rowIndex = 0;
                            }

                            const selectedCellLocation = new MeshLocation(colIndex, rowIndex);

                            const lastCtx = workbook.getMeshContextBySheetId(sheetSlotLocation.sheet.id);

                            meshSelection.selectCell(lastCtx, selectedCellLocation);

                        });
                    }, 100); // TODO: !?
                }
                else
                {
                    frameDetails.enterNestedView(
                        rootWorkbook.id,
                        sheetId,
                        sheetId,
                        meshState.details.activeCell.toSlotLocation()
                    ).then((frameDetailsEntry:FrameDetailsEntry) =>
                    {
                    });
                }

            });
        }
    }

    @bind
    private _getSelectedNestedPath():FramePath
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( !sampleDesign.selectedNestedPath ) return null;

        const framePath:FramePath = [];

        const re = /(\w+[\w-]*)\/?(\d+)?/g;

        const pairs = sampleDesign.selectedNestedPath.match(re);

        if( !pairs ) return null;

        pairs.forEach((pair:string) =>
        {
            const parts = pair.split('/');
            let rowIndex = parseInt(parts[1]) || 0;

            if( rowIndex > 0 ) rowIndex -= 1;
            if( rowIndex < 0 ) rowIndex = 0;

            framePath.push(new FrameCellCoordinate(parts[0], rowIndex));
        });

        return framePath;
    }

    @bind
    private _checkSelectedComponentName():void
    {
        const {sampleDesign} = rootState.ui.visualDesigner;
        const {designState} = commonControllers;

        if( !sampleDesign.selectedComponentName ) return;

        const page = designState.getActivePage();

        const {surfaceArea} = controllers.ui;

        const boxes = surfaceArea.getAllBoxes(page);

        let box:Box = null;

        boxes.forEach((_box:Box) =>
        {
            if( _box.boxType === BoxTypes.Viewport || box )
                return;

            const boxName = surfaceArea.getBoxName(_box);

            if( boxName === sampleDesign.selectedComponentName )
                box = _box;
        });

        const {surfaceSelection} = controllers.ui;

        if( box )
        {
            surfaceSelection.addBoxToSelection(box);
        }
        else
        {
            if( boxes.length == 1 ) // only viewport
                return;

            const childrenLength = boxes[0].children.length;

            if( childrenLength )
                surfaceSelection.addBoxToSelection(boxes[0].children[childrenLength - 1]);
        }
    }

    public switchToSurfaceOrSpreadSheet()
    {
        this._switch(false);
    }

    public switchToFlow()
    {
        this._switch(true);
    }

    private _switch(toFlow:boolean)
    {
        const currentRouteProps = rootState.navigation.currentRouteProps;

        const location = window.location;

        const newSearch = location.search.replace(
            `showFlowEditor=${toFlow ? 0 : 1}`,
            `showFlowEditor=${toFlow ? 1 : 0}`
        );

        let newLocation = `${location.pathname}${newSearch}`;

        if( currentRouteProps.history )
            currentRouteProps.history.push(newLocation);
    }

    public async grabSampleDesign(designId:number, siteId:string):Promise<SampleDesignResponse>
    {
        return API.sample
            .grabSampleDesign(designId, {organizationSiteId: siteId})
            .then(action((response:SampleDesignResponse | ApiError) =>
            {
                if( response instanceof ApiError )
                {
                    controllers.ui.toasts.showError(new Error(response.message));
                    return null;
                }
                else
                {
                    return response;
                }
            }));
    }
}
